import { z } from 'zod';
import { MINIMAL_SKIN } from '@sh/engine';
// Re-exported so the client depends on one package for the whole wire contract.
export { DEFAULT_SKIN, MINIMAL_SKIN } from '@sh/engine';
/**
 * Which skins this deployment actually has, asked for once by every client.
 *
 * The list is what the server found on disk, so an operator adds or removes a
 * skin by adding or removing a directory, and turns the feature off altogether
 * with one environment variable - no client build knows the names in advance.
 */
export const SKIN_CATALOG_PATH = '/api/skins';
/**
 * What kind of deployment this is, asked for once per tab like the skin
 * catalog above.
 *
 * A client cannot work this out for itself and must not guess. The bundle is
 * built once and served by every deployment, so `import.meta.env.DEV` is baked
 * `false` into the copy a dev server hands out; and under `pnpm dev` the page
 * comes from Vite rather than from this server, so nothing spliced into the
 * HTML is there to read either. Only the server knows, so the server is asked.
 *
 * `maxBots` is how many bots it will seat in one go, and `0` is how a live
 * deployment says it seats none - the client needs the number anyway to cap
 * its own buttons, so it carries the answer rather than a second flag.
 */
export const DEPLOYMENT_PATH = '/api/deployment';
/**
 * What a client assumes before the server answers, and for good if it never
 * does. Named here rather than left as a `?? false` at a call site, for the
 * same reason `NO_SKINS` is: the failure mode is the contract, not an accident
 * of whichever component asked first.
 */
export const LIVE_DEPLOYMENT = { env: 'live', maxBots: 0 };
/**
 * How long a lobby can outlive its last move. The server drops one a day after
 * its last transition, and it is here rather than on the server because a
 * phone needs the same number: a code is worth remembering - and offering back
 * as a way in - for exactly as long as the table behind it can still exist.
 */
export const LOBBY_TTL_MS = 24 * 60 * 60 * 1000;
/**
 * Where the game proxies Umami. A phone only ever talks to the origin that
 * served it the game, so the analytics host appears nowhere in a page and needs
 * no public name of its own. See apps/server/src/server.ts.
 */
export const TRACKER_SCRIPT_PATH = '/api/script.js';
export const TRACKER_COLLECT_PATH = '/api/send';
/**
 * Routing, analytics paths and page metadata all come from one declaration.
 * Re-exported here so every existing import site keeps working - `routes.ts`
 * is the file to edit, this is the front door.
 */
export { CODED_ROUTES, INDEXABLE_URLS, OTHER, ROUTES, TRACKED_PATHS, routeDefFor, routePattern, routeUrlFor, } from './routes.js';
export { CLIENT_EVENTS, isClientEvent } from './analytics.js';
export { FAQ_ENTRIES } from './faq.js';
/** What a client assumes before the server answers, and if it never does. */
export const NO_SKINS = { enabled: false, default: MINIMAL_SKIN, skins: [] };
/**
 * Lobby codes avoid characters that are misread when shouted across a table or
 * squinted at on a QR fallback: I, L, O, U, 0, 1.
 */
export const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTVWXYZ23456789';
export const CODE_LENGTH = 4;
export const codeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .length(CODE_LENGTH)
    .regex(new RegExp(`^[${CODE_ALPHABET}]+$`), 'not a valid lobby code');
export const nameSchema = z.string().trim().min(1, 'name required').max(20);
export const pinSchema = z.string().trim().regex(/^\d{4}$/, 'PIN must be four digits');
/**
 * Actions a client is allowed to send. The actor is taken from the socket's
 * authenticated seat and injected server-side, never trusted from the payload.
 */
export const clientActionSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('player_leave') }),
    z.object({ type: z.literal('rename'), name: nameSchema }),
    z.object({ type: z.literal('host_reorder_seats'), seatOrder: z.array(z.string()).min(1).max(10) }),
    z.object({ type: z.literal('host_kick'), targetId: z.string() }),
    z.object({
        type: z.literal('host_set_config'),
        config: z.object({
            skin: z.string().max(40).optional(),
        }),
    }),
    z.object({ type: z.literal('host_start_game') }),
    z.object({ type: z.literal('ack_reveal') }),
    z.object({ type: z.literal('nominate'), nomineeId: z.string() }),
    z.object({ type: z.literal('cast_ballot'), ja: z.boolean() }),
    z.object({ type: z.literal('president_discard'), index: z.number().int().min(0).max(2) }),
    z.object({ type: z.literal('chancellor_enact'), index: z.number().int().min(0).max(1) }),
    z.object({ type: z.literal('request_veto') }),
    z.object({ type: z.literal('respond_veto'), consent: z.boolean() }),
    z.object({ type: z.literal('power_investigate'), targetId: z.string() }),
    z.object({ type: z.literal('power_special_election'), targetId: z.string() }),
    z.object({ type: z.literal('power_ack') }),
    z.object({ type: z.literal('power_execute'), targetId: z.string() }),
    z.object({ type: z.literal('host_abort') }),
    z.object({ type: z.literal('host_return_to_lobby') }),
]);
export const createPayloadSchema = z.object({ name: nameSchema });
export const joinPayloadSchema = z.object({ code: codeSchema, name: nameSchema });
export const resumePayloadSchema = z.object({
    code: codeSchema,
    playerId: z.string().min(1).max(64),
    token: z.string().min(1).max(128),
});
export const claimPayloadSchema = z.object({ code: codeSchema, pin: pinSchema });
export const boardJoinPayloadSchema = z.object({ code: codeSchema });
export const issuePinPayloadSchema = z.object({ targetId: z.string() });
/**
 * How many bots a host may seat in one go.
 *
 * Ten seats minus the host who is asking, which is the most that can ever fit.
 * Asking for more is not an error - the server seats as many as the table has
 * room for and says how many that was, because a double-tap at nine seats
 * should put one bot down rather than raise a banner.
 */
export const MAX_BOTS = 9;
/** Four bots plus a host is the smallest legal game, so it is the default. */
export const DEFAULT_BOTS = 4;
export const addBotsPayloadSchema = z.object({
    count: z.number().int().min(1).max(MAX_BOTS).default(DEFAULT_BOTS),
});
/**
 * A practice table: one person and bots, dealt straight away. `players` is the
 * whole table, the person included, so it is the same 5-10 the rules use and
 * the same number the size picker shows.
 */
export const PRACTICE_PLAYERS = { min: 5, max: 10, default: 7 };
export const practicePayloadSchema = z.object({
    name: nameSchema,
    players: z.number().int().min(PRACTICE_PLAYERS.min).max(PRACTICE_PLAYERS.max),
});
/**
 * Where a player says something back.
 *
 * The one thing this application accepts that is not a move in a game, and so
 * the only place free text crosses the wire at all. Everything about the shape
 * below exists to stop that becoming a second channel for the things the rest
 * of this codebase works to keep off it.
 */
export const FEEDBACK_PATH = '/api/feedback';
/**
 * Five hundred characters: a paragraph, not an essay.
 *
 * It also sits well under ntfy's 4,096-byte ceiling, and that ceiling matters
 * more than it looks - ntfy does not reject a message above it, it silently
 * turns it into an attachment file, so feedback would arrive as something to
 * download rather than something to read. ASCII-only (below) makes a character
 * a byte, so 500 here is 500 there and that conversion is unreachable rather
 * than merely unlikely.
 */
export const FEEDBACK_MAX = 500;
/**
 * Letters, digits, space, newline and basic punctuation. ASCII, deliberately.
 *
 * The punctuation set is picked so a bug report can still carry a URL, which
 * needs `: / . - _ ? & = # @ %`. Left out: `<`, `>`, backslash and the
 * backtick, so nothing arriving here can read as markup or markdown in
 * whichever client the operator happens to read it in.
 *
 * The cost is real and worth stating plainly: an accented character, a
 * non-Latin script and an emoji are all refused, so this asks people to write
 * in ASCII. The client tests the same pattern before anything is sent and
 * names the characters to remove, because the usual way to trip this is a
 * phone's own smart quotes and em dashes - which the person did not type and
 * cannot see as a category.
 */
export const FEEDBACK_ALLOWED = /^[A-Za-z0-9 \n.,;:!?'"()[\]\-_/@#&+=%$]*$/;
/**
 * Characters nobody types on purpose: the C0 and C1 control ranges minus tab
 * and newline, plus the zero-width and bidi family.
 *
 * Stripped rather than refused, because they are invisible - telling somebody
 * their message contains U+200B is not an error they can do anything about.
 * Anything *visible* that is not allowed is refused instead, with a message,
 * because quietly editing what somebody wrote is worse than declining it.
 */
const FEEDBACK_STRIP = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2060\uFEFF\u2066-\u2069]/g;
export const feedbackMessageSchema = z
    .string()
    // Bounds the work the transform does before it starts. The route's body
    // limit already covers this; it costs one line not to depend on that.
    .max(FEEDBACK_MAX * 4, 'too long')
    .transform((raw) => raw
    // Folds the fullwidth forms back to ASCII, so a keyboard that produced
    // them is corrected rather than refused.
    .normalize('NFKC')
    .replace(/\r\n?/g, '\n')
    .replace(FEEDBACK_STRIP, '')
    // Runs of spaces and tabs collapse. Newlines are left alone here,
    // because a paragraph break carries meaning in a bug report.
    .replace(/[^\S\n]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim())
    .pipe(z
    .string()
    .min(1, 'say something first')
    .max(FEEDBACK_MAX, `keep it under ${FEEDBACK_MAX} characters`)
    .regex(FEEDBACK_ALLOWED, 'letters, numbers and basic punctuation only'));
/**
 * The whole payload. One field, and `.strict()` is the point of it.
 *
 * A body carrying `{ message, code, name }` is refused outright rather than
 * quietly narrowed to its first field. The analytics collector rebuilds its
 * payload from an allowlist because its caller is a third-party script that
 * cannot be told anything; the caller here is this repository's own bundle, so
 * refusing is strictly better - it fails loudly, in a test, the first time
 * anybody tries to attach an identity to a message.
 */
export const feedbackPayloadSchema = z.object({ message: feedbackMessageSchema }).strict();
export const EVENTS = {
    create: 'create',
    join: 'join',
    resume: 'resume',
    claim: 'claim',
    boardJoin: 'board:join',
    action: 'action',
    issuePin: 'host:issue-pin',
    /**
     * Seat bots at this table. Only a dev deployment honours it; a live one
     * answers with a refusal rather than going quiet, because an unanswered
     * socket event with an ack is a spinner that never stops.
     *
     * Not a `ClientAction`: it is not a move, it changes no game state, and the
     * engine knows nothing about bots. `host:issue-pin` above is the same shape
     * for the same reasons. The `dev:` prefix says so at every call site.
     */
    addBots: 'dev:add-bots',
    /**
     * Open a practice table: the caller seated as host, bots in every other
     * seat, and the game dealt. Answers like `create`, with the caller's seat.
     */
    practice: 'practice:create',
    view: 'view',
    board: 'board',
    evicted: 'evicted',
};
//# sourceMappingURL=index.js.map